import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  useRive,
  Layout,
  Fit,
  Alignment,
  Bounds,
} from '@rive-app/react-webgl2';
import isMobileContext from 'contexts/IsMobile/IsMobileContext';
import { CommunityShotItem, ShotRemix } from 'types/community';
import ShotMetadata from './ShotMetadata';
import CommunityShotNotReady from './CommunityShotNotReady';
import StateMachineInputs, { useInputs } from './StateMachineInputsWebGL2';
import styles from './CommunityShot.module.css';

import CommentSection from '../comment/CommentSection';
import RiveTabList, { RiveTab } from 'components/common/RiveTabList';
import RemixesSection from 'components/community/shot/RemixesSection';
import { join } from 'lib/helpers';
import { useWindowSize } from 'lib/deviceUtils';
import { VoidCallback } from 'types/callbacks';
import featureFlags from 'lib/featureFlags';
import LevaMarketplaceControls from './LevaMarketplaceControls';

function getRiveParameters(shotItem?: CommunityShotItem) {
  if (!shotItem || !shotItem.isReadyForViewing) {
    return undefined;
  }
  const baseParameters = {
    src: shotItem.fileUrl || undefined,
    artboard: shotItem.artboard,
    autoplay: true,
    autoBind: true,
    layout: new Layout({
      fit: Fit.Cover,
      alignment: Alignment.Center,
    }),
  };

  if (shotItem.isAnimationStateMachine) {
    return {
      ...baseParameters,
      // Let runtime handle special events like opening a URL via OpenUrlEvent
      stateMachines: shotItem.animation,
    };
  }

  return {
    ...baseParameters,
    animations: shotItem.animation,
  };
}

interface UseResizeCanvasProps {
  /**
   * Whether or not Rive is loaded and renderer is associated with the canvas
   */
  riveLoaded: boolean;
  /**
   * Ref to the canvas element
   */
  canvasElem: HTMLCanvasElement | null;
  /**
   * Ref to the container element of the canvas
   */
  containerElem: HTMLElement | null;
  /**
   * (Optional) Callback to be invoked after the canvas has been resized due to a resize
   * of its parent container. This is where you would want to reset the layout
   * dimensions for the Rive renderer to dictate the new min/max bounds of the
   * canvas.
   *
   * Using the high-level JS runtime, this might be a simple call to `rive.resizeToCanvas()`
   * Using the low-level JSruntime, this might be invoking the renderer's `.align()` method
   * with the Layout and min/max X/Y values of the canvas.
   *
   * @returns void
   */
  onCanvasHasResized?: () => void;
  artboardBounds?: Bounds;
}

class FakeResizeObserver {
  observe() {}
  unobserve() {}
  disconnect() {}
}

function throttle(f: Function, delay: number) {
  let timer = 0;
  return function (this: Function, ...args: any) {
    clearTimeout(timer);
    timer = window.setTimeout(() => f.apply(this, args), delay);
  };
}
const MyResizeObserver = globalThis.ResizeObserver || FakeResizeObserver;

const hasResizeObserver = globalThis.ResizeObserver !== undefined;
const useResizeObserver = hasResizeObserver;
const useWindowListener = !useResizeObserver;
type Dimensions = {
  width: number;
  height: number;
};

function useSize(containerElem: HTMLElement | null) {
  const [size, setSize] = useState<Dimensions>({
    width: 0,
    height: 0,
  });

  // internet explorer does not support ResizeObservers.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleResize = () => {
        setSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      };

      if (useWindowListener) {
        // only pay attention to window size changes when we do not have the resizeObserver (IE only)
        handleResize();
        window.addEventListener('resize', handleResize);
      }

      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);
  const observer = useRef(
    new MyResizeObserver(
      throttle((entries: any) => {
        if (useResizeObserver) {
          setSize({
            width: entries[entries.length - 1].contentRect.width,
            height: entries[entries.length - 1].contentRect.height,
          });
        }
      }, 0)
    )
  );

  useEffect(() => {
    const currentObserver = observer.current;
    if (containerElem && useResizeObserver) {
      currentObserver.observe(containerElem);
    }

    return () => {
      currentObserver.disconnect();
      if (containerElem && useResizeObserver) {
        currentObserver.unobserve(containerElem);
      }
    };
  }, [containerElem, observer]);

  return size;
}

function getDevicePixelRatio(): number {
  const hasDprProp =
    typeof window !== 'undefined' &&
    typeof window.devicePixelRatio === 'number';
  const dpr = hasDprProp ? window.devicePixelRatio : 1;
  return Math.min(Math.max(1, dpr), 3);
}

export function useDevicePixelRatio() {
  const dpr = getDevicePixelRatio();
  const [currentDpr, setCurrentDpr] = useState(dpr);

  useEffect(() => {
    const canListen = typeof window !== 'undefined' && 'matchMedia' in window;
    if (!canListen) {
      return;
    }

    const updateDpr = () => {
      const newDpr = getDevicePixelRatio();
      setCurrentDpr(newDpr);
    };
    const mediaMatcher = window.matchMedia(
      `screen and (resolution: ${currentDpr}dppx)`
    );
    // eslint-disable-next-line no-prototype-builtins
    mediaMatcher.hasOwnProperty('addEventListener')
      ? mediaMatcher.addEventListener('change', updateDpr)
      : mediaMatcher.addListener(updateDpr);

    return () => {
      // eslint-disable-next-line no-prototype-builtins
      mediaMatcher.hasOwnProperty('removeEventListener')
        ? mediaMatcher.removeEventListener('change', updateDpr)
        : mediaMatcher.removeListener(updateDpr);
    };
  }, [currentDpr]);

  return currentDpr;
}

export function useResizeCanvas({
  riveLoaded = false,
  canvasElem,
  containerElem,
  onCanvasHasResized,
  artboardBounds,
}: UseResizeCanvasProps) {
  const [
    { height: lastContainerHeight, width: lastContainerWidth },
    setLastContainerDimensions,
  ] = useState<Dimensions>({
    height: 0,
    width: 0,
  });
  const [
    { height: lastCanvasHeight, width: lastCanvasWidth },
    setLastCanvasSize,
  ] = useState<Dimensions>({
    height: 0,
    width: 0,
  });

  const [isFirstSizing, setIsFirstSizing] = useState(true);

  const containerSize = useSize(containerElem);
  const currentDevicePixelRatio = useDevicePixelRatio();

  const { maxX, maxY } = artboardBounds ?? {};

  const getContainerDimensions = useCallback(() => {
    const width = containerElem?.clientWidth ?? 0;
    const height = containerElem?.clientHeight ?? 0;
    if (artboardBounds) {
      const { maxY, maxX } = artboardBounds;
      return { width, height: width * (maxY / maxX) };
    }
    return {
      width,
      height,
    };
  }, [containerElem, maxX, maxY]);

  useEffect(() => {
    // If Rive is not ready, the container is not ready, or the user supplies a flag
    // to not resize the canvas to the container, then return early
    if (!containerElem || !riveLoaded) {
      return;
    }

    const { width, height } = getContainerDimensions();
    let hasResized = false;
    if (canvasElem) {
      // We are in full screen mode, do different calcs..
      if (document.fullscreenElement !== null) {
        // container get fit to the screen.
        const rivRatio = width / height;
        const screenRatio = screen.width / screen.height;
        let containerHeight = screen.height;
        let containerWidth = screen.width;
        if (rivRatio > screenRatio) {
          containerHeight = screen.width * (1 / rivRatio);
        } else if (rivRatio < screenRatio) {
          containerWidth = screen.height * rivRatio;
        }
        const newCanvasWidthProp = currentDevicePixelRatio * containerWidth;
        const newCanvasHeightProp = currentDevicePixelRatio * containerHeight;
        containerElem.style.display = 'flex';
        containerElem.style.justifyContent = 'center';
        canvasElem.width = newCanvasWidthProp;
        canvasElem.height = newCanvasHeightProp;
        canvasElem.style.height = `${containerHeight}px`;
        canvasElem.style.width = `${containerWidth}px`;
        canvasElem.style.margin = 'auto';
        setLastCanvasSize({
          width: newCanvasWidthProp,
          height: newCanvasHeightProp,
        });
        setLastContainerDimensions({
          width: containerWidth,
          height: containerHeight,
        });
        hasResized = true;
      } else {
        // Check if the canvas parent container bounds have changed and set
        // new values accordingly
        const boundsChanged =
          width !== lastContainerWidth || height !== lastContainerHeight;
        containerElem.style.height = height + 'px';
        hasResized = true;
        // Check if devicePixelRatio may have changed and get new canvas
        // width/height values to set the size
        const canvasSizeChanged =
          width * currentDevicePixelRatio !== lastCanvasWidth ||
          height * currentDevicePixelRatio !== lastCanvasHeight;
        if (boundsChanged || canvasSizeChanged) {
          const newCanvasWidthProp = currentDevicePixelRatio * width;
          const newCanvasHeightProp = currentDevicePixelRatio * height;
          canvasElem.width = newCanvasWidthProp;
          canvasElem.height = newCanvasHeightProp;
          canvasElem.style.width = width + 'px';
          canvasElem.style.height = height + 'px';
          setLastCanvasSize({
            width: newCanvasWidthProp,
            height: newCanvasHeightProp,
          });
          hasResized = true;
        }
        setLastContainerDimensions({ width, height });
      }
    }

    // Callback to perform any Rive-related actions after resizing the canvas
    // (i.e., reset the Rive layout in the render loop)
    if (onCanvasHasResized && (isFirstSizing || hasResized)) {
      onCanvasHasResized && onCanvasHasResized();
    }
    isFirstSizing && setIsFirstSizing(false);
  }, [
    canvasElem,
    containerElem,
    containerSize,
    currentDevicePixelRatio,
    getContainerDimensions,
    isFirstSizing,
    setIsFirstSizing,
    lastCanvasHeight,
    lastCanvasWidth,
    lastContainerHeight,
    lastContainerWidth,
    onCanvasHasResized,
    riveLoaded,
  ]);

  // Reset width and height values when the canvas changes
  useEffect(() => {
    setLastCanvasSize({
      width: 0,
      height: 0,
    });
  }, [canvasElem]);
}

function useDisplaySize(
  initialWidth: number | null,
  initialHeight: number | null,
  windowWidth: number,
  windowHeight: number
) {
  const [displayDimensions, setDisplayDimensions] = useState({
    width: initialWidth ?? 0,
    height: initialHeight ?? 0,
  });

  // 956 to fit the comment section
  const maxAnimationWidth = 956;
  const maxAnimationHeight = 956;

  useEffect(() => {
    if (initialWidth === null || initialHeight === null) {
      setDisplayDimensions({ width: 0, height: 0 });
    }

    const { width: animationWidth, height: animationHeight } =
      displayDimensions;

    const aspectRatio = animationWidth / animationHeight;

    const maxHeight = Math.min(windowHeight - 80, maxAnimationHeight);

    let desiredHeight = maxHeight;
    let desiredWidth = desiredHeight * aspectRatio;

    const maxWidth = Math.min(windowWidth - 80, maxAnimationWidth);

    if (desiredWidth > maxWidth) {
      desiredWidth = maxWidth;
      desiredHeight = desiredWidth / aspectRatio;
    }

    setDisplayDimensions({ width: desiredWidth, height: desiredHeight });
  }, [windowWidth, windowHeight, initialWidth, initialHeight]);

  return displayDimensions;
}

type Props = {
  shotItem: CommunityShotItem;
  remixes: ShotRemix[];
};

export default function CommunityShot({ shotItem, remixes }: Props) {
  const { isMobile, isAppMounted } = useContext(isMobileContext);
  const [selectedTab, setSelectedTab] = useState('actions');
  const [disableReactionsTray, setDisableReactionsTray] = useState(false);
  const [scrollToComments, setScrollToComments] = useState(false);

  const screenRef = useRef<HTMLDivElement>(null);

  const commentsRef = React.useRef<HTMLDivElement>(null);

  const riveParams = getRiveParameters(shotItem);

  const riveOptions = {
    fitCanvasToArtboardHeight: false,
    shouldResizeCanvasToContainer: false,
    useDevicePixelRatio: false,
  };

  const { RiveComponent, rive, canvas, container } = useRive(
    riveParams,
    riveOptions
  );

  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const { width: displayWidth, height: displayHeight } = useDisplaySize(
    shotItem.videoWidth,
    shotItem.videoHeight,
    windowWidth,
    windowHeight
  );

  const onCanvasHasResized = useCallback(() => {
    if (rive) {
      rive.startRendering();
      rive.resizeToCanvas();
    }
  }, [rive]);

  useResizeCanvas({
    riveLoaded: rive !== null,
    canvasElem: canvas,
    containerElem: container,
    artboardBounds: rive?.bounds,
    onCanvasHasResized,
  });

  useEffect(() => {
    setSelectedTab(isMobile ? 'actions' : 'comments');
  }, [isMobile]);

  useEffect(() => {
    if (scrollToComments && selectedTab === 'comments' && commentsRef.current) {
      commentsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
      setScrollToComments(false);
    }
  }, [scrollToComments, selectedTab]);

  function onCommentsClick() {
    setSelectedTab('comments');
    setScrollToComments(true);
  }

  const inputsReady =
    Boolean(shotItem) &&
    shotItem?.isReadyForViewing &&
    shotItem?.isAnimationStateMachine;

  const inputs = useInputs({
    rive,
    stateMachineName: shotItem?.animation,
    isReady: inputsReady,
  });

  // Attempt to fit the animation in view, otherwise fallback to letting the
  // animation take whatever space it wants. We do this to stop really wide
  // or tall artboards taking up too much space.
  const riveStyle = displayWidth
    ? {
        height: displayHeight,
        width: displayWidth,
      }
    : {};

  function onFullscreenClick() {
    function onFullscreenExit() {
      if (canvas) {
        document.removeEventListener('fullscreenchange', onFullscreenExit);
      }
    }
    if (container) {
      document.addEventListener('fullscreenchange', onFullscreenExit);
      container.requestFullscreen();
    }
  }

  return (
    <div className={styles.CommunityShotContainer}>
      <div className={styles.CommunityShotMainColumn}>
        <div
          style={riveStyle}
          ref={screenRef}
          className={join([
            styles.CommunityShotAnimationContainer,
            'checkerboard',
          ])}
        >
          <RiveComponent className={styles.CommunityShotAnimation} />
        </div>

        <div className={styles.CommunityShotActions}>
          <RiveTabList
            onTabSelect={(value) => setSelectedTab(value)}
            selectedTab={selectedTab}
            fitContentWidth={!isMobile}
          >
            {isMobile && <RiveTab value="actions">Actions</RiveTab>}
            <RiveTab value="comments">Comments</RiveTab>
            <RiveTab value="remixes">Remixes</RiveTab>
          </RiveTabList>
          {isAppMounted && (
            <FullscreenButton onFullscreenClick={onFullscreenClick} />
          )}
        </div>
        <div
          className={
            selectedTab === 'comments' || selectedTab === 'remixes'
              ? ''
              : styles.HiddenOnMobile
          }
        >
          {selectedTab === 'remixes' && (
            <RemixesSection shotItem={shotItem} remixes={remixes} />
          )}
          {selectedTab === 'comments' && (
            <CommentSection ref={commentsRef} shotItem={shotItem} />
          )}
        </div>
      </div>

      {!shotItem.isReadyForViewing && <CommunityShotNotReady />}

      <div className={selectedTab === 'actions' ? '' : styles.HiddenOnMobile}>
        <ShotMetadata
          shotItem={shotItem}
          onCommentsClick={onCommentsClick}
          disableReactionsTray={disableReactionsTray}
        >
          {!featureFlags.dataBinding() && inputs && rive && (
            <StateMachineInputs
              inputs={inputs}
              rive={rive}
              onDragChange={(isDragging) => setDisableReactionsTray(isDragging)}
            />
          )}
          {featureFlags.dataBinding() &&
            rive &&
            shotItem.isAnimationStateMachine && (
              <div className={styles.LevaContainer}>
                <LevaMarketplaceControls rive={rive} inputs={inputs} />
              </div>
            )}
        </ShotMetadata>
      </div>
    </div>
  );
}

type FullscreenButtonProps = {
  onFullscreenClick: VoidCallback;
};

function FullscreenButton({ onFullscreenClick }: FullscreenButtonProps) {
  if (featureFlags.fullscreenMode() && document && document.fullscreenEnabled) {
    return (
      <button
        className={styles.CommunityShotFullScreen}
        onClick={onFullscreenClick}
      >
        Full Screen
      </button>
    );
  }
  return null;
}
